import {
  VILLA_SPACE,
  WORLD_SLUG,
  posesFor,
  servedWorldsOf,
  spaceOfBlock,
  type PhotoSpotId,
  type PieceInfo,
  type PiecePose,
  type PlacementPhoto,
  type PlacementSpace,
  type ServedWorld,
} from '@vrlearning/lesson-schema';
import { furnitureAssignedOf } from './api/session-client.js';

/**
 * The console's window onto a meubelset (ADR-037; spec "What the console's viewer iframe does"):
 * the headset app at `?viewer=<ticket>`, embedded by the console as it embeds a lesson's preview.
 * No XR, no socket, no pairing; the ticket -- single use, a minute -- fetches the set's pieces,
 * their signed URLs and, for a result, a saved arrangement. It answers the console by postMessage:
 *
 * | Console → viewer | Viewer → console |
 * | --- | --- |
 * | `{type: 'piece', pieceId}`: that piece, turnable, beside a 1.5 m figure | – |
 * | `{type: 'thumbnails'}` | `{type: 'thumbnail', pieceId, png}` per ready piece, 320 × 240 |
 * | `{type: 'result'}` | `{type: 'plan', png}` 2000 × 1300, then `{type: 'photo', spot, png}` × the world's photos (the villa's four), 1600 × 1000 |
 * | – | `{type: 'ready'}` once loaded; `{type: 'trouble', text}` in Dutch |
 *
 * The villa by default; an answer that names another `world` (with its served package in `worlds` and
 * the package's signed files in `media`) is that world's placement space: its plan from its floors,
 * tables and walls, and the photos its plaatsruimte names (`spot` "foto-1", "foto-2"...).
 *
 * It listens only to the console origin the ticket's answer names, and posts only to it. The images
 * are drawn here, in the teacher's browser, from the saved poses; nothing is uploaded.
 */

/** What `GET /v1/furniture-viewer/{ticket}` answers. */
export type ViewerAnswer = {
  readonly setTitle: string;
  readonly pieces: readonly PieceInfo[];
  readonly media: Readonly<Record<string, string>>;
  /** A saved arrangement's pieces (the Postgres jsonb), or null for a set's own preview. */
  readonly arrangement: { readonly pieces: Readonly<Record<string, unknown>> } | null;
  readonly consoleOrigin: string;
  /** The world the set is placed in; absent, the villa. */
  readonly world?: string;
  /** The served packages by slug: the one `world` names, when it is not the villa. */
  readonly worlds?: Readonly<Record<string, ServedWorld>>;
};

/**
 * Where the pieces may stand in the answer's world: the villa's plan unless it names a package with a
 * plaatsruimte; undefined when it names a world that has none (nothing can be drawn).
 */
export function spaceOfAnswer(
  answer: Pick<ViewerAnswer, 'world' | 'worlds'>,
): PlacementSpace | undefined {
  const slug = answer.world ?? 'villa';
  const served = answer.worlds?.[slug];
  if (!served) return slug === 'villa' ? VILLA_SPACE : undefined;
  const block = served.package.blocks.find((b) => b.type === 'plaatsruimte');
  return block?.type === 'plaatsruimte' ? spaceOfBlock(slug, block) : undefined;
}

/** The names a result's photos go by: the villa's rooms, else "foto-1"... in the order of the space. */
export function photoSpots(space: Pick<PlacementSpace, 'world' | 'photos'>): string[] {
  return space.world === 'villa'
    ? [...PHOTO_ORDER]
    : space.photos.map((_, index) => `foto-${index + 1}`);
}

/** The console's words, in Dutch and formal: a teacher reads them. */
export const VIEWER_TEXT = {
  gone: 'Deze voorvertoning is verlopen. Open de meubelset opnieuw.',
  failed: 'De voorvertoning kon niet worden geladen.',
  noPiece: 'Dit stuk is er niet (meer).',
  drawing: 'De tekening lukt niet in deze browser.',
} as const;

/** The photos of a result, in the order they are sent. */
export const PHOTO_ORDER: readonly PhotoSpotId[] = ['woonkamer', 'keuken', 'slaapkamer', 'terras'];

/** The images' sizes, pixels. */
export const VIEWER_SIZES = {
  thumbnail: [320, 240],
  plan: [2000, 1300],
  photo: [1600, 1000],
} as const;

/** Reads `?viewer=<ticket>` and takes it off the address at once (a used ticket is worthless). */
export function takeViewerRequest(
  place: { href: string } | undefined = globalThis.location,
  history: Pick<History, 'replaceState'> | undefined = globalThis.history,
): { ticket: string } | undefined {
  let url: URL;
  try {
    url = new URL(place?.href ?? '');
  } catch {
    return undefined;
  }
  const ticket = url.searchParams.get('viewer');
  if (!ticket) return undefined;
  history?.replaceState(null, '', url.pathname);
  return { ticket };
}

export type ViewerOutcome =
  { kind: 'ready'; answer: ViewerAnswer } | { kind: 'gone' } | { kind: 'failed'; detail: string };

/** Redeems the ticket, uncached. Used, expired and never issued are one 404. */
export async function fetchViewer(
  ticket: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<ViewerOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`/v1/furniture-viewer/${encodeURIComponent(ticket)}`, {
      cache: 'no-store',
    });
  } catch {
    return { kind: 'failed', detail: 'geen verbinding met de server' };
  }
  if (response.status === 404) return { kind: 'gone' };
  if (!response.ok) return { kind: 'failed', detail: `fout ${response.status}` };
  let body: Record<string, unknown>;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    return { kind: 'failed', detail: 'onleesbaar antwoord' };
  }
  const set = furnitureAssignedOf({ setTitle: body['setTitle'], pieces: body['pieces'] });
  const origin = body['consoleOrigin'];
  if (!set || typeof origin !== 'string' || !isOrigin(origin))
    return { kind: 'failed', detail: 'onvolledig antwoord' };
  const media = typeof body['media'] === 'object' && body['media'] !== null ? body['media'] : {};
  const arrangement = body['arrangement'];
  const world =
    typeof body['world'] === 'string' && WORLD_SLUG.test(body['world']) ? body['world'] : undefined;
  return {
    kind: 'ready',
    answer: {
      ...(world ? { world, worlds: servedWorldsOf(body['worlds']) } : {}),
      setTitle: set.setTitle,
      pieces: set.pieces,
      media: media as Record<string, string>,
      arrangement:
        typeof arrangement === 'object' &&
        arrangement !== null &&
        typeof (arrangement as Record<string, unknown>)['pieces'] === 'object'
          ? (arrangement as ViewerAnswer['arrangement'])
          : null,
      consoleOrigin: origin,
    },
  };
}

/** An origin and nothing else (`https://host:port`): postMessage's target is never `*` or a URL. */
function isOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return url.origin !== 'null' && url.origin === value;
  } catch {
    return false;
  }
}

/** What the viewer needs drawn (render/furniture/viewer-scene.ts; a fake in tests). */
export type ViewerRenderer = {
  /** Every piece fetched and parsed; the ids of those that could be. */
  load(answer: ViewerAnswer): Promise<string[]>;
  /** One piece on the grey floor beside the figure, turnable with the mouse. */
  showPiece(pieceId: string): void;
  thumbnail(pieceId: string): Promise<Blob>;
  /** The world furnished as `poses` say: the plan from above, and a photo from each of its spots. */
  plan(poses: Readonly<Record<string, PiecePose>>): Promise<Blob>;
  photo(photo: PlacementPhoto, poses: Readonly<Record<string, PiecePose>>): Promise<Blob>;
};

export type ViewerMessage =
  | { type: 'ready' }
  | { type: 'trouble'; text: string }
  | { type: 'thumbnail'; pieceId: string; png: Blob }
  | { type: 'plan'; png: Blob }
  | { type: 'photo'; spot: string; png: Blob };

/**
 * The viewer's side of the conversation: loads the set, says `ready`, and answers what the console
 * asks -- only the console the ticket names, one request at a time.
 */
export class ViewerHost {
  readonly #answer: ViewerAnswer;
  readonly #renderer: ViewerRenderer;
  readonly #post: (message: ViewerMessage) => void;
  readonly #space: PlacementSpace | undefined;
  #ready: string[] = [];
  #queue: Promise<void> = Promise.resolve();

  constructor(input: {
    answer: ViewerAnswer;
    renderer: ViewerRenderer;
    /** Posts to the console, at its origin only. */
    post: (message: ViewerMessage) => void;
  }) {
    this.#answer = input.answer;
    this.#renderer = input.renderer;
    this.#post = input.post;
    this.#space = spaceOfAnswer(input.answer);
  }

  /** Where the pieces stand in a result: the saved poses, or their garage slots. */
  get poses(): Record<string, PiecePose> {
    const ready = new Set(this.#ready);
    return posesFor(
      this.#answer.pieces.filter((piece) => ready.has(piece.id)),
      this.#answer.arrangement?.pieces,
      this.#space,
    );
  }

  async start(): Promise<void> {
    // A world without a placement space cannot be drawn.
    if (!this.#space) {
      this.#post({ type: 'trouble', text: VIEWER_TEXT.failed });
      return;
    }
    try {
      this.#ready = await this.#renderer.load(this.#answer);
    } catch {
      this.#post({ type: 'trouble', text: VIEWER_TEXT.failed });
      return;
    }
    this.#post({ type: 'ready' });
  }

  /** A message from the window: acted on only from the console's origin. */
  receive(event: { origin: string; data: unknown }): Promise<void> {
    if (event.origin !== this.#answer.consoleOrigin) return Promise.resolve();
    const data = event.data;
    if (typeof data !== 'object' || data === null) return Promise.resolve();
    const message = data as Record<string, unknown>;
    // One at a time: a result's five images are not interleaved with thumbnails.
    this.#queue = this.#queue
      .then(() => this.#handle(message))
      .catch(() => {
        this.#post({ type: 'trouble', text: VIEWER_TEXT.drawing });
      });
    return this.#queue;
  }

  async #handle(message: Record<string, unknown>): Promise<void> {
    switch (message['type']) {
      case 'piece': {
        const id = message['pieceId'];
        if (typeof id !== 'string' || !this.#ready.includes(id)) {
          this.#post({ type: 'trouble', text: VIEWER_TEXT.noPiece });
          return;
        }
        this.#renderer.showPiece(id);
        return;
      }
      case 'thumbnails':
        for (const pieceId of this.#ready) {
          this.#post({ type: 'thumbnail', pieceId, png: await this.#renderer.thumbnail(pieceId) });
        }
        return;
      case 'result': {
        const poses = this.poses;
        this.#post({ type: 'plan', png: await this.#renderer.plan(poses) });
        const space = this.#space!;
        const spots = photoSpots(space);
        for (const [index, photo] of space.photos.entries()) {
          this.#post({
            type: 'photo',
            spot: spots[index]!,
            png: await this.#renderer.photo(photo, poses),
          });
        }
        return;
      }
      default:
        return;
    }
  }
}
